//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { createContext, useContext } from 'react';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import IItemMetadata, {
  Product,
  UiaProductTagRequest,
} from '@models/IItemMetadata';

export interface ProductTaggingContext {
  addProduct: (
    newProduct: UiaProductTagRequest,
    productTags: Product[],
  ) => void;
  bulkTagProducts: (
    selectedProducts: CuralateCatalogProduct[],
    productTags: Product[],
  ) => void;
  cancelNewSpatialTag: () => void;
  catalogClientId: ClientId | undefined;
  cuid: Cuid;
  editProduct: ({
    productList,
    updatedProduct,
  }: {
    productList: Product[];
    updatedProduct: UiaProductTagRequest;
  }) => void;
  handlePageChange: (currentPage: number, itemsPerPage: number) => void;
  hasCatalogConnected: boolean;
  isCatalogSuggestions: boolean | undefined;
  isLoadingProducts: boolean;
  isProductEditingFeatureDisabled: boolean; // this is sometimes set within showroom flows
  isAddingOrRemovingProduct: boolean;
  isProcessingProductTagsMutation: boolean;
  isTaggingProducts: boolean;
  limit: number;
  metadata: IItemMetadata;
  offset: number;
  products?: CuralateCatalogProduct[];
  removeProduct: ({
    listOfProducts,
    productStyleId,
  }: {
    productStyleId: string;
    listOfProducts: Product[];
  }) => void;
  reorderProducts: ({
    reorderedProducts,
  }: {
    reorderedProducts: Product[];
  }) => void;
  searchQuery: string;
  selectedProductToEdit?: Product | CuralateCatalogProduct;
  setCatalogClientId: (clientId: ClientId | undefined) => void;
  setIsTaggingProducts: (isTagging: boolean) => void;
  setLimit: (limit: number) => void;
  setOffset: (offset: number) => void;
  setSearchQuery: (query: string) => void;
  setSelectedProductToEdit: (
    product?: Product | CuralateCatalogProduct,
  ) => void;
  shouldShowCatalogSelector: boolean;
  totalCount: number;
}

export const ProductTaggingContext =
  createContext<ProductTaggingContext | null>(null);

export const useProductTaggingContext = () => {
  const context = useContext(ProductTaggingContext);
  if (!context) {
    throw new Error(
      'useProductTaggingContext must be used within a ProductTaggingProvider',
    );
  }
  return context;
};
